// utils/initNotificationSocket.js
import { io } from "socket.io-client";
import { useNotificationStore } from "../Store/notificationStore";
import { useOfferStore } from "../Store/offerStore";
import { useBflowStore } from "../Store/bflowStore";
import detectPlatform from "./detectPlatform";
import decodeToken from "../lib/decodeToken";
import {
  getPendingDial,
  clearPendingDial,
  isCallAfterDial,
  routeForCall,
} from "../lib/bflowPostCall";

let socketInstance = null;

// Maujooda socket, banata nahi. initNotificationSocket() ko bina userId ke
// call karna khatarnak hai — wo register(undefined) wala socket bana deta hai.
export const getNotificationSocket = () => socketInstance;

// Detect if running inside React Native WebView
const isNativeApp = () => !!window.ReactNativeWebView;

// The sales-agent offer flow (satisfaction check → tiered plan offers) is a
// NATIVE-APP-ONLY experience. Gate it on detectPlatform (android/ios) — NOT on
// the raw window.ReactNativeWebView, which can be momentarily false on a refresh
// before the bridge re-injects. detectPlatform is refresh-safe (localStorage
// cache), so it matches the same gate every other part of the flow uses
// (isMobileAppSurface, RecommendedPlan → GoLiveVariant, GlobalCallBanner, …).
const isAppSurface = () => {
  // Unified flow: the sales socket handlers (satisfaction_level, plan_recommendation,
  // go_live_confirmed, silent_mode) now run on EVERY surface — web included — so
  // web behaves the same as the app. To restore per-platform behaviour, revert to:
  //   const p = detectPlatform(); return p === "android" || p === "ios";
  return true;
};

// Helper to register push token with backend (works for both web + native)
export const registerPushToken = async (userId, token, platform = "web", isEnabled = true) => {
  if (!token || !userId) return;
  try {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:2512/api";
    const authToken = localStorage.getItem("token");
    await fetch(`${baseUrl}/push/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
      },
      body: JSON.stringify({
        userId,
        token,
        platform, // "web" | "android" | "ios"
        isEnabled,
        deviceInfo: platform === "web"
          ? { userAgent: navigator.userAgent }
          : { platform, source: "native_app" }
      })
    });
    console.log(`[Push] ${platform} token registered successfully (isEnabled=${isEnabled}).`);
  } catch (e) {
    console.error("[Push] Token registration failed:", e);
  }
};

let lastSyncedStatus = null;

// Helper to update permission status for an existing device token
export const updatePushPermissionStatus = async (userId, token, isEnabled, platform) => {
  if (!userId) return;
  const targetPlatform = platform || localStorage.getItem("fcmPlatform") || (detectPlatform() === "ios" ? "ios" : (detectPlatform() === "android" ? "android" : "web"));
  const targetToken = token || localStorage.getItem("fcmToken");

  if (!targetToken && !targetPlatform) return;

  // Track status in localStorage so app restarts remember the OS state
  if (isNativeApp()) {
    try {
      localStorage.setItem("nativeNotifStatus", isEnabled ? "granted" : "denied");
    } catch (_e) { }
  }

  try {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:2512/api";
    const authToken = localStorage.getItem("token");
    await fetch(`${baseUrl}/push/update-permission`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
      },
      body: JSON.stringify({
        userId,
        token: targetToken || undefined,
        platform: targetPlatform,
        isEnabled
      })
    });
    console.log(`[Push] Device push permission updated: isEnabled=${isEnabled} (platform=${targetPlatform})`);
  } catch (e) {
    console.error("[Push] Update permission failed:", e);
  }
};

export const syncStoredPushToken = async (userId) => {
  if (!userId) return;
  const token = localStorage.getItem("fcmToken");
  const platform = localStorage.getItem("fcmPlatform") || (detectPlatform() === "ios" ? "ios" : (detectPlatform() === "android" ? "android" : "web"));
  if (token) {
    // If on web and user blocked notification in browser settings, tell backend to skip this token
    if (!isNativeApp() && typeof window !== "undefined" && "Notification" in window) {
      if (Notification.permission === "denied") {
        await updatePushPermissionStatus(userId, token, false, "web");
        return;
      }
    }
    // If on native and cached OS status is denied, update status as disabled
    if (isNativeApp() && localStorage.getItem("nativeNotifStatus") === "denied") {
      await updatePushPermissionStatus(userId, token, false, platform);
      return;
    }
    await registerPushToken(userId, token, platform, true);
  }
};

let isBridgeInitialized = false;
// Setup native app message listener (FCM token + notifications from RN bridge)
export const setupNativeMessageBridge = () => {
  if (typeof window === "undefined" || isBridgeInitialized) return;
  isBridgeInitialized = true;
  const handleNativeMessage = (event) => {
    try {
      let raw = event.data;
      if (typeof raw === "string") {
        try {
          raw = JSON.parse(raw);
        } catch {
          return;
        }
      }
      if (!raw || typeof raw !== "object") return;

      const type = raw.type;
      // RN wraps messages in envelope: { __fromRN: true, type, data, ts } or un-enveloped { type, ... }
      const payload = raw.__fromRN ? raw.data : (raw.data !== undefined ? raw.data : raw);

      // Native app sends FCM token after permission grant
      if (type === "FCM_TOKEN") {
        const token = payload?.token || raw.token;
        const platform = payload?.platform || raw.platform || (detectPlatform() === "ios" ? "ios" : "android");
        if (token) {
          console.log(`[Push] Native FCM token received (${platform}):`, token.substring(0, 20) + "...");
          localStorage.setItem("fcmToken", token);
          localStorage.setItem("fcmPlatform", platform);

          // If user is currently logged in, register immediately
          const authToken = localStorage.getItem("token");
          const uid = authToken ? decodeToken(authToken)?.id : null;
          if (uid) {
            const isDenied = localStorage.getItem("nativeNotifStatus") === "denied";
            registerPushToken(uid, token, platform, !isDenied);
          }
        }
      }

      // Native app forwards push notification data (tapped from background/killed)
      if (type === "PUSH_NOTIFICATION" || type === "NOTIFICATION_OPENED") {
        const remoteMsg = payload || {};
        const notification = remoteMsg.notification || remoteMsg;
        const clickAction = remoteMsg.data?.clickAction || remoteMsg.clickAction || "";
        useNotificationStore.getState().addNotification({
          title: notification?.title || "Notification",
          message: notification?.body || "",
          type: "push",
          clickAction: clickAction,
          createdAt: new Date().toISOString(),
          status: "unread",
        });
      }

      if (type === "PERMISSION_RESULT") {
        const perm = payload?.permission || raw.permission;
        const status = payload?.status || raw.status;
        if (perm === "NOTIFICATIONS" && status) {
          const authToken = localStorage.getItem("token");
          const uid = authToken ? decodeToken(authToken)?.id : null;
          const token = payload?.token || raw.token || localStorage.getItem("fcmToken");
          const platform = payload?.platform || raw.platform || (detectPlatform() === "ios" ? "ios" : "android");
          const isGranted = status === "granted";
          if (uid) {
            updatePushPermissionStatus(uid, token, isGranted, platform);
          }
        }
        window.dispatchEvent(new CustomEvent("native_permission_result", { detail: raw }));
      }
    } catch (err) {
      console.log("[Push] PERMISSION_RESULT received:", err);
    }
  };

  // iOS uses window, Android uses document
  window.addEventListener("message", handleNativeMessage);
  document.addEventListener("message", handleNativeMessage);

  // Signal native shell that WebView is ready to receive messages
  if (typeof window !== "undefined" && window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === "function") {
    try {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: "WEB_READY" }));
    } catch (_e) { }
  }

  // If already logged in on bootstrap, sync any previously saved token
  const authToken = localStorage.getItem("token");
  const uid = authToken ? decodeToken(authToken)?.id : null;
  if (uid) {
    syncStoredPushToken(uid);
  }
};

// Setup web push token (Firebase web SDK).
// Note: foreground banner display is auto-wired inside fetchToken() once a
// token is obtained, so we don't need to subscribe a listener here.
const setupWebPush = (userId) => {
  if (typeof window === "undefined" || !("Notification" in window)) return;

  const storedToken = localStorage.getItem("fcmToken");

  if (Notification.permission === "denied") {
    if (storedToken && userId) {
      updatePushPermissionStatus(userId, storedToken, false);
    }
    return;
  }

  if (Notification.permission === "granted") {
    import("../firebase").then(async ({ fetchToken, setupTokenRefreshListener }) => {
      try {
        const token = await fetchToken();
        if (token) {
          localStorage.setItem("fcmToken", token);
          localStorage.setItem("fcmPlatform", "web");
          registerPushToken(userId, token, "web", true);
        }
        setupTokenRefreshListener((newToken) => {
          if (newToken) {
            localStorage.setItem("fcmToken", newToken);
            localStorage.setItem("fcmPlatform", "web");
            registerPushToken(userId, newToken, "web", true);
          }
        });
      } catch (e) {
        console.error("[Push] Web token fetch failed:", e);
      }
    });
  }

  // Dynamic check for browser site settings change (e.g. user toggles permissions in Chrome)
  if (typeof navigator !== "undefined" && navigator.permissions && navigator.permissions.query) {
    try {
      navigator.permissions.query({ name: "notifications" }).then((permissionStatus) => {
        permissionStatus.onchange = () => {
          const currentToken = localStorage.getItem("fcmToken");
          const authToken = localStorage.getItem("token");
          const uid = authToken ? decodeToken(authToken)?.id : userId;
          if (permissionStatus.state === "denied") {
            if (currentToken && uid) {
              updatePushPermissionStatus(uid, currentToken, false);
            }
          } else if (permissionStatus.state === "granted") {
            if (uid) {
              setupWebPush(uid);
            }
          }
        };
      }).catch(() => { });
    } catch (e) {
      // ignore
    }
  }
};

export const initNotificationSocket = (userId, navigate) => {
  if (socketInstance) return socketInstance;

  const baseUrl = process.env.REACT_APP_API_BASE_URL
    ? process.env.REACT_APP_API_BASE_URL.split("/api")[0]
    : "http://localhost:2512";

  socketInstance = io(baseUrl, {
    transports: ["websocket"],
  });

  socketInstance.on("connect", () => {
    socketInstance.emit("register", userId);

    // Push token registration — auto-detect platform
    if (isNativeApp()) {
      // Native app: listen for FCM token via bridge
      setupNativeMessageBridge();
      syncStoredPushToken(userId);
      console.log("[Push] Native app detected — listening for FCM_TOKEN via bridge");
    } else {
      // Web: use Firebase web SDK
      setupWebPush(userId);
      console.log("[Push] Web detected — using Firebase web SDK");
    }
  });

  socketInstance.on("notification", (message) => {
    useNotificationStore.getState().addNotification(message);

    // Forward notification to native app for local display (if in WebView)
    if (isNativeApp()) {
      try {
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: "SOCKET_NOTIFICATION",
          data: message,
        }));
      } catch (e) { /* ignore */ }
    }
  });

  // satisfaction_level tool: positive → collapse the "Meet Sophia" popup into
  // the banner ON THE SAME /fast-step screen (no navigation). Negative = no UI
  // change (the agent handles it verbally). Offers come later via Plan_recommendation.
  socketInstance.on("satisfaction_level", (payload) => {
    // Native-app-only sales flow — ignore entirely on web.
    if (!isAppSurface()) return;
    console.log("[Socket] satisfaction_level received:", payload);
    try {
      if (payload?.positive) useOfferStore.getState().promote();
    } catch (e) {
      /* ignore */
    }
  });

  // Sales agent's tiered-offer tool: show the right offer on the plan screen.
  //   plans → open the full plan picker; trial → 21-day trial; payg → $5/mo.
  // We stash the tier in the offer store (screens read it reactively) and make
  // sure the user is on /recommended-plan so those screens are mounted.
  // Plan_recommendation tool: show the right offer. tier "plans" opens the plan
  // picker modal over the CURRENT screen (no navigation). "trial"/"payg" are
  // full pages on /recommended-plan.
  socketInstance.on("plan_recommendation", (payload) => {
    // Native-app-only sales flow — ignore entirely on web.
    if (!isAppSurface()) return;
    console.log("[Socket] plan_recommendation received:", payload);
    try {
      const tier = payload?.tier || "plans";
      useOfferStore.getState().setOffer(tier, payload?.recommended_plan || null);
      if (
        (tier === "trial" || tier === "payg") &&
        typeof navigate === "function" &&
        window.location.pathname !== "/recommended-plan"
      ) {
        navigate("/recommended-plan");
      }
    } catch (e) {
      /* ignore */
    }
  });

  // silent_mode tool: the user asked the agent to cut/stop the call. Instead of
  // ending, the agent goes quiet and we just CLOSE the call popup — the call
  // itself keeps running in the persistent banner. Native-app-only.
  socketInstance.on("silent_mode", (payload) => {
    if (!isAppSurface()) return;
    console.log("[Socket] silent_mode received:", payload);
    try {
      useOfferStore.getState().goSilent();
    } catch (e) {
      /* ignore */
    }
  });

  // confirm_go_live tool: the owner clearly agreed to proceed with the shown
  // offer. Reveal the pay button on the current $5 (GoLivePayg) / $20
  // (TrialOffer) screen. Native-app-only sales flow.
  socketInstance.on("go_live_confirmed", (payload) => {
    if (!isAppSurface()) return;
    console.log("[Socket] go_live_confirmed received:", payload);
    try {
      useOfferStore.getState().setAgreed();
    } catch (e) {
      /* ignore */
    }
  });

  /*
   * A/B variant B ka post-call gate.
   *
   * /agent-live pe user apne PHONE se agent ko call karta hai. Wo call app ke
   * BAHAR hoti hai, isliye app khud kabhi nahi jaan sakta ki wo khatam hui —
   * backend ka call_analyzed webhook ye event bhejta hai.
   *
   *   call >= 30 sec → /call-summary   (Sophia ne kya capture kiya)
   *   call <  30 sec → /21-day-trial
   *
   * Ye handler tabhi kuch karta hai jab localStorage me pending dial ho, yaani
   * user ne sach me is flow me "Call ... Now" dabaya ho. Baaki har call
   * (dashboard, purane agents, asli customers) chup-chaap ignore ho jaati hai.
   */
  /*
   * Asli phone call abhi-abhi CONNECT hui.
   *
   * Iske bina /agent-live ka modal "Waiting for call to connect" pe hi atka
   * rehta tha — user baat kar raha hota aur 45s baad usse "No call detected"
   * dikh jaata. Ab wo "Call in progress" pe chala jaata hai aur timeout ruk
   * jaata hai.
   *
   * Sirf tab jab is flow me pending gate ho aur agent match kare.
   */
  socketInstance.on("phone_call_started", (payload) => {
    console.log("[Socket] phone_call_started received:", payload);
    try {
      const pending = getPendingDial();
      if (!pending || payload?.agentId !== pending.agentId) return;
      useBflowStore.getState().startCall();
    } catch (e) {
      /* ignore */
    }
  });

  socketInstance.on("phone_call_completed", (payload) => {
    console.log("[Socket] phone_call_completed received:", payload);
    try {
      const pending = getPendingDial();
      if (!pending) return;                                   // is flow me nahi hain
      if (payload?.agentId !== pending.agentId) return;        // doosra agent
      if (!isCallAfterDial(payload, pending.dialedAt)) return; // web call / purani call

      clearPendingDial(); // pehle saaf karo — wapas aakar dobara na chale

      /*
       * Lambi call (>= 30s) → koi navigation NAHI.
       *
       * Pehle ye seedha /call-summary pe bhej deta tha. Ab call detect hote hi
       * user ko USI /agent-live screen pe "Call captured" card dikhta hai, aur
       * detail tab khulti hai jab wo "Click for details" dabaye. Isliye yahan
       * bas store me rakh dete hain — screen use padh leti hai.
       *
       * Store me rakhna surakshit hai chahe user dashboard chala gaya ho: kuch
       * uchhalta nahi, wo dobara /agent-live pe aayega tabhi card dikhega.
       */
      if (routeForCall(payload) === "/call-summary") {
        useBflowStore.getState().setCapturedCall(payload);
        return;
      }

      /*
       * Chhoti call (< 30s) → trial screen. Ye NAVIGATION hai, isliye pehle
       * dekh lo ki user abhi bhi flow me hai. Call ke doran wo dashboard ja
       * sakta hai; usse kheench kar wapas laana galat hai.
       */
      if (window.location.pathname !== "/agent-live") {
        console.log("[Socket] phone_call_completed — user flow se bahar hai, skip");
        return;
      }
      if (typeof navigate === "function") {
        navigate("/21-day-trial", { replace: true });
      }
    } catch (e) {
      /* ignore — gate fail hone par user bas /agent-live pe raha rahega */
    }
  });

  socketInstance.on("disconnect", () => {
    console.log("❌ Socket Disconnected");
  });

  return socketInstance;
};
